// src/components/operation/CashWithdrawal.js
import React, { useState } from 'react';
import { Modal, InputNumber, Select, Input, Button, message, Form, Segmented, Alert } from 'antd';
import { MinusCircleOutlined, PlusCircleOutlined, SwapOutlined } from '@ant-design/icons';
import operationsService from '../../services/operationsService';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { TextArea } = Input;

const WITHDRAWAL_REASONS = [
    { value: 'security',         label: 'Seguridad / Resguardo' },
    { value: 'supplier_payment', label: 'Pago a proveedor' },
    { value: 'expense',          label: 'Gasto operativo' },
    { value: 'bank_deposit',     label: 'Depósito bancario' },
    { value: 'other',            label: 'Otro' },
];

const DEPOSIT_REASONS = [
    { value: 'change_fund',      label: 'Fondo de cambio' },
    { value: 'petty_cash',       label: 'Caja chica' },
    { value: 'reimbursement',    label: 'Reintegro / Reembolso' },
    { value: 'bank_withdrawal',  label: 'Extracción bancaria' },
    { value: 'other',            label: 'Otro' },
];

const MODE = {
    withdrawal: {
        label: 'Retiro',
        amountLabel: 'Monto a retirar',
        placeholder: 'Seleccione el motivo del retiro',
        reasons: WITHDRAWAL_REASONS,
        confirm: 'Confirmar Retiro',
        successMsg: 'Retiro registrado exitosamente',
        icon: <MinusCircleOutlined />,
        color: '#ff4d4f',
        titleText: 'Retiro de Caja',
        service: (data) => operationsService.recordWithdrawal(data),
    },
    deposit: {
        label: 'Ingreso manual',
        amountLabel: 'Monto a ingresar',
        placeholder: 'Seleccione el motivo del ingreso',
        reasons: DEPOSIT_REASONS,
        confirm: 'Confirmar Ingreso',
        successMsg: 'Ingreso registrado exitosamente',
        icon: <PlusCircleOutlined />,
        color: '#52c41a',
        titleText: 'Ingreso Manual de Caja',
        service: (data) => operationsService.recordDeposit(data),
    },
};

function CashWithdrawal({ visible, onClose, onSuccess }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState('withdrawal');
    const [noOperationError, setNoOperationError] = useState(false);

    const cfg = MODE[mode];

    const handleModeChange = (val) => {
        setMode(val);
        setNoOperationError(false);
        form.resetFields();
    };

    const handleCancel = () => {
        form.resetFields();
        setMode('withdrawal');
        setNoOperationError(false);
        onClose();
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        setNoOperationError(false);
        try {
            await cfg.service({
                amount: values.amount,
                reason: values.reason,
                description: values.description || null,
            });
            message.success(cfg.successMsg);
            form.resetFields();
            handleCancel();
            if (onSuccess) onSuccess();
        } catch (error) {
            if (error.response?.status === 404) {
                setNoOperationError(true);
            } else {
                console.error('[CashWithdrawal] Error:', error);
                message.error('Error al registrar el movimiento');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={
                <span>
                    <SwapOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                    Retiro / Ingreso Manual
                </span>
            }
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={500}
            destroyOnClose
        >
            {/* Mode switcher */}
            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center' }}>
                <Segmented
                    value={mode}
                    onChange={handleModeChange}
                    size="large"
                    options={[
                        {
                            label: (
                                <span>
                                    <MinusCircleOutlined style={{ marginRight: 6, color: '#ff4d4f' }} />
                                    Retiro
                                </span>
                            ),
                            value: 'withdrawal',
                        },
                        {
                            label: (
                                <span>
                                    <PlusCircleOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                                    Ingreso manual
                                </span>
                            ),
                            value: 'deposit',
                        },
                    ]}
                />
            </div>

            {/* No open operation error */}
            {noOperationError && (
                <Alert
                    type="error"
                    showIcon
                    message="No hay una operación abierta. Iniciá la caja antes de registrar movimientos."
                    style={{ marginBottom: 16 }}
                />
            )}

            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                    name="amount"
                    label={cfg.amountLabel}
                    rules={[
                        { required: true, message: 'Ingrese el monto' },
                        { type: 'number', min: 0.01, message: 'Monto debe ser mayor a 0' },
                    ]}
                >
                    <InputNumber
                        prefix="$"
                        style={{ width: '100%' }}
                        precision={2}
                        size="large"
                        placeholder="0.00"
                        autoFocus
                    />
                </Form.Item>

                <Form.Item
                    name="reason"
                    label="Motivo"
                    rules={[{ required: true, message: 'Seleccione un motivo' }]}
                >
                    <Select
                        options={cfg.reasons}
                        placeholder={cfg.placeholder}
                        size="large"
                    />
                </Form.Item>

                <Form.Item name="description" label="Descripción adicional (opcional)">
                    <TextArea
                        rows={3}
                        placeholder="Detalles adicionales..."
                        maxLength={250}
                        showCount
                    />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        disabled={isDemoMode}
                        block
                        size="large"
                        danger={mode === 'withdrawal'}
                        style={mode === 'deposit' ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : {}}
                    >
                        {cfg.confirm}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
}

export default CashWithdrawal;
